import React, { useState, useEffect } from 'react';
import { auditAPI } from '../../api/api';
import { Save, BookOpen, Target, FileWarning, CalendarDays, Clipboard, StickyNote } from 'lucide-react';

const AuditPlanning = ({ engagement, onUpdate }) => {
  const planning = engagement?.planning || {};
  const [form, setForm] = useState({
    objectives: planning.objectives || '',
    scope: planning.scope || '',
    key_risk_areas: planning.key_risk_areas || '',
    departments_covered: planning.departments_covered || [],
    documents_requested: planning.documents_requested || '',
    schedule: planning.schedule || '',
    previous_audit_ref: planning.previous_audit_ref || '',
    notes: planning.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [deptInput, setDeptInput] = useState('');

  useEffect(() => {
    const p = engagement?.planning || {};
    setForm({
      objectives: p.objectives || '', scope: p.scope || '',
      key_risk_areas: p.key_risk_areas || '', departments_covered: p.departments_covered || [],
      documents_requested: p.documents_requested || '', schedule: p.schedule || '',
      previous_audit_ref: p.previous_audit_ref || '', notes: p.notes || '',
    });
  }, [engagement]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await auditAPI.updateEngagement(engagement.id, { planning: form });
      if (onUpdate) onUpdate();
    } catch (e) { console.error(e); alert('Failed to save planning'); }
    setSaving(false);
  };

  const addDept = () => {
    if (deptInput.trim() && !form.departments_covered.includes(deptInput.trim())) {
      setForm({ ...form, departments_covered: [...form.departments_covered, deptInput.trim()] });
      setDeptInput('');
    }
  };

  const removeDept = (d) => setForm({ ...form, departments_covered: form.departments_covered.filter(x => x !== d) });

  const fields = [
    { key: 'objectives', label: 'Audit Objectives', icon: Target, rows: 3, placeholder: 'Define the objectives of this audit engagement...' },
    { key: 'scope', label: 'Scope of Audit', icon: BookOpen, rows: 3, placeholder: 'Define audit scope, areas to be covered...' },
    { key: 'key_risk_areas', label: 'Key Risk Areas', icon: FileWarning, rows: 2, placeholder: 'Identify key risk areas to focus on...' },
    { key: 'documents_requested', label: 'Documents Requested', icon: Clipboard, rows: 2, placeholder: 'List documents requested from client...' },
    { key: 'schedule', label: 'Audit Schedule', icon: CalendarDays, rows: 2, placeholder: 'Planned dates, milestones...' },
    { key: 'previous_audit_ref', label: 'Previous Audit Reference', icon: StickyNote, rows: 1, placeholder: 'Reference to previous audit report...' },
    { key: 'notes', label: 'Notes', icon: StickyNote, rows: 2, placeholder: 'Additional planning notes...' },
  ];

  return (
    <div className="space-y-4" data-testid="audit-planning-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800">Audit Planning</h3>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          data-testid="save-planning-btn"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Planning'}
        </button>
      </div>

      <div className="grid gap-4">
        {fields.map(f => (
          <div key={f.key} className="bg-white rounded-xl border p-4">
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <f.icon className="w-4 h-4 text-indigo-500" /> {f.label}
            </label>
            <textarea
              value={form[f.key]}
              onChange={e => setForm({ ...form, [f.key]: e.target.value })}
              rows={f.rows}
              placeholder={f.placeholder}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              data-testid={`planning-${f.key}`}
            />
          </div>
        ))}

        {/* Departments Covered */}
        <div className="bg-white rounded-xl border p-4">
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
            Departments Covered
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={deptInput}
              onChange={e => setDeptInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDept())}
              placeholder="Add department..."
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
              data-testid="planning-dept-input"
            />
            <button onClick={addDept} className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200" data-testid="add-dept-btn">Add</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {form.departments_covered.map((d, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">
                {d}
                <button onClick={() => removeDept(d)} className="hover:text-red-500 ml-0.5">&times;</button>
              </span>
            ))}
            {form.departments_covered.length === 0 && <span className="text-xs text-gray-400">No departments added</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditPlanning;
